/**
 * Exchange Rate Monitor.
 *
 * Shows the rates the portal is actually holding, where each came from, and —
 * the point of a monitor rather than a table — whether the feed is still
 * arriving. A stale rate is not visibly different from a fresh one on an order
 * line, so staleness is surfaced here explicitly.
 *
 * Rates are stored as "1 GBP = rate x CURRENCY". The inverse is shown beside
 * each rate because that is the direction people quote day to day.
 */
import { useState, useEffect, useCallback } from 'react';
import { coreAPI } from '../../lib/api';
import type { ExchangeRateRow, MonthlyExchangeRates } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import AddExchangeRateForm from './AddExchangeRateForm';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Coins, Loader2, AlertCircle, RefreshCw, TriangleAlert } from 'lucide-react';

const ALL = 'ALL';

/** A rate older than this is called out — the ERP feed runs far more often. */
const STALE_AFTER_DAYS = 35;

type ViewMode = 'monthly' | 'history';

/** Six decimals, matching how the rate is stored. */
function fmtRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(6);
}

/** 1 unit of the currency expressed in the base — the everyday direction. */
function fmtInverse(value: string | number | null | undefined): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n === 0) return '—';
  return (1 / n).toFixed(6);
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-08" -> "Aug 2026". A static table, not toLocaleDateString: the repo
 *  bans locale date APIs in favour of the shared formatters, and a month label
 *  has no locale-sensitive content worth the exception. */
function formatMonth(ym: string): string {
  const [year, month] = ym.split('-');
  const name = MONTH_NAMES[Number(month) - 1];
  return name ? `${name} ${year}` : ym;
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return Number.POSITIVE_INFINITY;
  return Math.floor((Date.now() - then) / 86_400_000);
}

/** Where a rate came from. The ERP feed reports itself as `api`. */
function sourceLabel(source: string): string {
  if (source === 'api') return 'ERP feed';
  if (source === 'manual') return 'Manual';
  if (source === 'fallback') return 'Fallback';
  return source;
}

export default function ExchangeRatesPage() {
  const { formatDate } = useFormatters();

  const [view, setView] = useState<ViewMode>('monthly');
  const [currency, setCurrency] = useState<string>(ALL);
  const [monthly, setMonthly] = useState<MonthlyExchangeRates | null>(null);
  const [rows, setRows] = useState<ExchangeRateRow[]>([]);
  const [latest, setLatest] = useState<{ rates: Record<string, string>; as_of: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  // Mirrors the backend's admin gate (IsHelixOrSupplierAdmin): anyone else is
  // told the page is admin-only without calling the endpoints.
  const { user } = useAuth();
  const { isCustomer } = usePortalMode();
  const isAdmin = !!(user?.platform === 'HELIX' || user?.is_superuser
    || (user?.is_company_admin && !isCustomer));

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const params = currency === ALL ? {} : { currency };
      const [latestRes, dataRes] = await Promise.all([
        coreAPI.getLatestExchangeRates(),
        view === 'monthly'
          ? coreAPI.getMonthlyExchangeRates({ ...params, months: 24 })
          : coreAPI.getExchangeRates({ ...params, page_size: 100 }),
      ]);
      setLatest({ rates: latestRes.data.rates, as_of: latestRes.data.as_of });
      if (view === 'monthly') {
        setMonthly(dataRes.data as MonthlyExchangeRates);
        setRows([]);
      } else {
        const paged = dataRes.data as { results?: ExchangeRateRow[] };
        setRows(paged.results ?? (dataRes.data as unknown as ExchangeRateRow[]));
        setMonthly(null);
      }
    } catch (err: unknown) {
      // Non-admins reach this page only by typing its URL; the history is
      // admin-only on the backend. Say that once instead of three messages.
      if ((err as { response?: { status?: number } }).response?.status === 403) {
        setForbidden(true);
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Could not load exchange rates: ${message}`);
    } finally {
      setLoading(false);
    }
  }, [view, currency, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const knownCurrencies = monthly?.currencies
    ?? Array.from(new Set(rows.map((r) => r.currency))).sort();

  const staleDays = latest?.as_of ? daysSince(latest.as_of) : Number.POSITIVE_INFINITY;
  const isStale = Number.isFinite(staleDays) && staleDays > STALE_AFTER_DAYS;
  const hasNoRates = !latest?.as_of
    || Object.keys(latest.rates ?? {}).filter((c) => c !== 'GBP').length === 0;

  if (forbidden || !isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex items-center gap-2 pt-6 text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            Exchange rates are managed by administrators.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Coins className="h-6 w-6" />
            Exchange Rates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rates fed from the ERP (or entered below), used to convert foreign-currency
            orders to GBP. Stored as 1 GBP = rate.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {/* The monitor part: say plainly when there is nothing, or nothing recent. */}
      {hasNoRates && !loading && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="flex items-start gap-3 pt-6">
            <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">No exchange rates are stored.</p>
              <p className="text-muted-foreground mt-1">
                Foreign-currency orders cannot be converted to GBP until the ERP
                rate feed delivers. Order values in USD, EUR or PLN will not be
                comparable with GBP ones.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!hasNoRates && isStale && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="flex items-start gap-3 pt-6">
            <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">
                Rates have not updated for {staleDays} days.
              </p>
              <p className="text-muted-foreground mt-1">
                The newest rate is dated {formatDate(latest!.as_of)}. Conversions
                are still running on it, so values may not reflect current rates.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <AddExchangeRateForm onSaved={load} />

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 pt-6 text-destructive">
            <AlertCircle className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      )}

      {/* Current rates */}
      {latest && !hasNoRates && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Current rates
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                as of {formatDate(latest.as_of)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]">
              {Object.entries(latest.rates)
                .filter(([code]) => code !== 'GBP')
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([code, rate]) => (
                  <div key={code} className="border rounded-lg p-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      GBP → {code}
                    </div>
                    <div className="text-lg font-semibold tabular-nums">{fmtRate(rate)}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      1 {code} = {fmtInverse(rate)} GBP
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={view} onValueChange={(v) => setView(v as ViewMode)}>
          <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly summary</SelectItem>
            <SelectItem value="history">Full history</SelectItem>
          </SelectContent>
        </Select>

        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All currencies</SelectItem>
            {knownCurrencies.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : view === 'monthly' ? (
            monthly && monthly.months.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      {monthly.currencies.map((c) => (
                        <TableHead key={c} className="text-right">1 GBP = {c}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthly.months.map((month) => (
                      <TableRow key={month}>
                        <TableCell className="font-medium">{formatMonth(month)}</TableCell>
                        {monthly.currencies.map((c) => {
                          const cell = monthly.rates[month]?.[c];
                          return (
                            <TableCell key={c} className="text-right tabular-nums">
                              {cell ? fmtRate(cell.rate) : '—'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No rates stored yet.
              </p>
            )
          ) : rows.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">1 GBP =</TableHead>
                    <TableHead className="text-right">1 unit in GBP</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{formatDate(r.rate_date)}</TableCell>
                      <TableCell className="font-medium">{r.currency}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtRate(r.rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInverse(r.rate)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{sourceLabel(r.source)}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No rates stored yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
